import React, { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link as RouterLink } from '~/lib/react-router-dom';

import { OpenInNew } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridExpandMoreIcon,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoAdminPermission from '~/components/Promo/hooks/usePromoPermission';
import ProvideCreditAmountStatus from '~/components/Vouchers/ProvideCreditAmountStatus';
import { RewardAmount, type RewardAmountKind } from '~/components/Vouchers/RewardAmountCell';
import VoucherLogWarningsButton from '~/components/Vouchers/VoucherLogWarningsButton';
import VoucherLogWarningsSummaryButton from '~/components/Vouchers/VoucherLogWarningsSummaryButton';
import {
  type CreditGrantApplyLastResult,
  type CreditGrantDryRunLastResult,
  buildProcessCreditGrantColumnDef,
} from '~/components/Vouchers/voucherLogGridColumns';
import Table from '~/components/v2/common/Table/Table';

import { toPromoBrand } from '~/consts/promoBrand';

import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';
import { useProcessCreditGrantOrder } from '~/hooks/apis/promo/useProcessCreditGrantOrder';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { ListVoucherLogsQuery, PromoRequestFilters, VoucherLog } from '~/services/PromoService';

import { errorPayloadForDisplay } from '~/utils/creditGrantErrorPayload';
import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import { getVoucherLogDisplayMode, isVoucherLogProvideCredit } from '~/utils/voucherLogDisplayMode';
import { getVoucherLogWarningsSummary } from '~/utils/voucherLogWarnings';

import ColumnSelector from '../PromoTester/ColumnSelector';
import PromoTesterLink from '../PromoTester/PromoTesterLink';
import PromoCodeUser from '../formatters/PromoCodeUser';

import { DiscountBreakdownItem, PromoDiscountBreakdown } from './PromoDiscountDetails';
import VoucherEvaluationSummary from './VoucherEvaluationSummary';

interface PromoRequestVoucherLogsProps {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  voucherNames?: Array<string>;
  userId?: string;
  brand?: string;
  promoRequestsFilter?: PromoRequestFilters;
  setPromoRequestsFilter?: Dispatch<SetStateAction<PromoRequestFilters>>;
  updateUrlState?: (filter: {
    userId?: string;
    voucherName?: string;
    expanded?: boolean;
    itemsExpanded?: boolean;
  }) => void;
  setFormUserId?: (userId: string) => void;
  setFormVoucherName?: (voucherName: string) => void;
}

const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 1,
};

const DEFAULT_PAGE_SIZE = 20;

function getVoucherLogChannelMarkupId(log: VoucherLog): string | null | undefined {
  return (
    log.metadata?.orderItemMetadata?.itemModifierInfo?.channelMarkupId ??
    log.metadata?.itemMetadata?.itemModifierInfo?.channelMarkupId
  );
}

function PromoRequestVoucherLogs({
  expanded,
  onExpandedChange,
  voucherNames,
  userId,
  setPromoRequestsFilter,
  updateUrlState,
  setFormUserId,
  setFormVoucherName,
}: PromoRequestVoucherLogsProps) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    voucherLogId: false,
    voucherUserId: false,
    voucherDescription: false,
    voucherToggles: false,
    deletedAt: false,
    createdBy: false,
    processCreditGrant: true,
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [creditGrantDryRunLoadingLogId, setCreditGrantDryRunLoadingLogId] = useState<string | null>(null);
  const [creditGrantDryRunReadyLogId, setCreditGrantDryRunReadyLogId] = useState<string | null>(null);
  const [creditGrantProcessingLogId, setCreditGrantProcessingLogId] = useState<string | null>(null);
  const [creditGrantLastResult, setCreditGrantLastResult] = useState<CreditGrantApplyLastResult | null>(null);
  const [creditGrantDryRunLastResult, setCreditGrantDryRunLastResult] = useState<CreditGrantDryRunLastResult | null>(
    null,
  );

  const { enqueueSnackbar } = useSnackbar();
  const isPromoAdmin = usePromoAdminPermission();
  const processCreditGrantDryRunMutation = useProcessCreditGrantOrder();
  const processCreditGrantApplyMutation = useProcessCreditGrantOrder();

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  const handleFilterByVoucherName = useCallback(
    (voucherName: string) => {
      updateUrlState?.({ voucherName });
      setPromoRequestsFilter?.((prev) => ({ ...prev, voucherName }));
      setFormVoucherName?.(voucherName);
    },
    [updateUrlState, setPromoRequestsFilter, setFormVoucherName],
  );

  const handleFilterByUserId = useCallback(
    (nextUserId: string) => {
      updateUrlState?.({ userId: nextUserId, expanded: true, itemsExpanded: true });
      setPromoRequestsFilter?.((prev) => ({ ...prev, userId: nextUserId }));
      setFormUserId?.(nextUserId);
    },
    [updateUrlState, setPromoRequestsFilter, setFormUserId],
  );
  const tenant = useCurrentTenant();
  const brand = toPromoBrand(tenant.tenant.brand);

  const query = useMemo<ListVoucherLogsQuery>(() => {
    const q: ListVoucherLogsQuery = {
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      brand: brand,
      sortBy: 'created_at',
      sortByDirection: 'desc',
    };
    if (voucherNames?.length) {
      q.voucherNames = voucherNames;
    }
    if (userId) {
      q.userId = userId;
    }
    q.brand = brand;
    return q;
  }, [paginationModel, voucherNames, userId, brand]);

  const { data, isLoading, refetch: refetchVoucherLogs } = useListVoucherLogs({ query });
  const result = data?.result;
  const rows = useMemo(() => result?.logs ?? [], [result?.logs]);
  const total = Number(data?.result?.total ?? 0);
  const displayMode = useMemo(() => getVoucherLogDisplayMode(rows), [rows]);
  const voucherLogWarnings = useMemo(() => getVoucherLogWarningsSummary(result), [result]);

  const creditGrantMutationBusy =
    processCreditGrantDryRunMutation.isPending || processCreditGrantApplyMutation.isPending;

  const handleCreditGrantDryRun = useCallback(
    (log: VoucherLog) => {
      if (!isPromoAdmin) {
        enqueueSnackbar('Promo Admin role is required to process credit grants.', { variant: 'warning' });
        return;
      }
      const orderId = log.fkOrder ?? log.fk_order;
      if (orderId == null || String(orderId).trim() === '') {
        return;
      }
      const logId = String(log.voucherLogId ?? '');
      processCreditGrantApplyMutation.reset();
      setCreditGrantLastResult(null);
      setCreditGrantDryRunLastResult(null);
      setCreditGrantDryRunReadyLogId(null);
      setCreditGrantDryRunLoadingLogId(logId);
      processCreditGrantDryRunMutation.mutate(
        { orderIds: [String(orderId)], dryRun: true },
        {
          onSuccess: (data) => {
            setCreditGrantDryRunReadyLogId(logId);
            setCreditGrantDryRunLastResult({ voucherLogId: logId, data });
          },
          onError: (error) => {
            enqueueSnackbar(`Dry run failed: ${error instanceof Error ? error.message : String(error)}`, {
              variant: 'error',
            });
          },
          onSettled: () => {
            setCreditGrantDryRunLoadingLogId(null);
          },
        },
      );
    },
    [enqueueSnackbar, isPromoAdmin, processCreditGrantApplyMutation, processCreditGrantDryRunMutation],
  );

  const handleCreditGrantApply = useCallback(
    (log: VoucherLog) => {
      const orderId = log.fkOrder ?? log.fk_order;
      if (orderId == null || String(orderId).trim() === '') {
        return;
      }
      const logId = String(log.voucherLogId ?? '');
      setCreditGrantDryRunReadyLogId(null);
      setCreditGrantProcessingLogId(logId);
      processCreditGrantApplyMutation.mutate(
        { orderIds: [String(orderId)], dryRun: false },
        {
          onSuccess: async (data) => {
            enqueueSnackbar('Credit grant applied successfully.', { variant: 'success' });
            await refetchVoucherLogs();
            setCreditGrantDryRunLastResult(null);
            setCreditGrantLastResult({ voucherLogId: logId, outcome: 'success', data });
          },
          onError: (error) => {
            setCreditGrantLastResult({
              voucherLogId: logId,
              outcome: 'error',
              error: errorPayloadForDisplay(error),
            });
            enqueueSnackbar(`Credit grant failed: ${error instanceof Error ? error.message : String(error)}`, {
              variant: 'error',
            });
          },
          onSettled: () => {
            setCreditGrantProcessingLogId(null);
          },
        },
      );
    },
    [enqueueSnackbar, processCreditGrantApplyMutation, refetchVoucherLogs],
  );

  const columns: Array<GridColDef<VoucherLog>> = useMemo(() => {
    const dash = (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );

    const discountAmountCell = (params: GridRenderCellParams<VoucherLog>) => {
      const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
      const remaining = params.row.metadata?.remainingAmountAfter;
      return (
        <Typography component="span" color="success.main">
          <RewardAmount
            rewardAmount={params.row.amount}
            rewardKind="discount"
            creditLabel={currency}
            showUnknownAlert={false}
            component="span"
          />
          {remaining != null && remaining > 0 ? ` (${currencyFormatter(currency, remaining)} left)` : ''}
        </Typography>
      );
    };

    const creditAmountCell = (params: GridRenderCellParams<VoucherLog>) => {
      const rewardKind: RewardAmountKind =
        params.row.rewardKind === 'discount' ||
        params.row.rewardKind === 'provide_credit' ||
        params.row.rewardKind === 'provide_credit_percentage'
          ? params.row.rewardKind
          : params.row.reward_kind === 'discount' ||
              params.row.reward_kind === 'provide_credit' ||
              params.row.reward_kind === 'provide_credit_percentage'
            ? params.row.reward_kind
            : 'provide_credit';

      return (
        <Typography
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}
        >
          <RewardAmount
            rewardAmount={Number(params.row.amount)}
            rewardKind={rewardKind}
            creditLabel="Credit"
            showUnknownAlert={false}
            component="span"
          />
          <ProvideCreditAmountStatus row={params.row} />
        </Typography>
      );
    };

    const amountColumns: Array<GridColDef<VoucherLog>> =
      displayMode === 'discount-only'
        ? [
            {
              field: 'amount',
              headerName: 'Discount',
              ...sharedAttrs,
              renderCell: (params) => discountAmountCell(params),
            },
          ]
        : displayMode === 'provide-credit-only'
          ? [
              {
                field: 'amount',
                headerName: 'Credit payout',
                ...sharedAttrs,
                renderCell: (params) => creditAmountCell(params),
              },
            ]
          : [
              {
                field: 'amount',
                headerName: 'Discount',
                ...sharedAttrs,
                renderCell: (params) => (isVoucherLogProvideCredit(params.row) ? dash : discountAmountCell(params)),
              },
              {
                field: 'creditPayoutAmountDisplay',
                headerName: 'Credit payout',
                ...sharedAttrs,
                renderCell: (params) => (isVoucherLogProvideCredit(params.row) ? creditAmountCell(params) : dash),
              },
            ];

    const creditTimelineColumns: Array<GridColDef<VoucherLog>> =
      displayMode === 'discount-only'
        ? []
        : [
            {
              field: 'creditGrantedAt',
              headerName: 'Credit granted',
              ...sharedAttrs,
              valueGetter: (_value, row) => row.creditGrantedAt ?? row.metadata?.creditGrantedAt,
              renderCell: (params) => (params.value ? <DateFormatterDetailed date={params.value as string} /> : dash),
            },
            {
              field: 'creditPayoutAt',
              headerName: 'Credit payout at',
              ...sharedAttrs,
              valueGetter: (_value, row) => row.metadata?.creditPayoutAt,
              renderCell: (params) => {
                const payoutAt = params.row.metadata?.creditPayoutAt;
                if (!payoutAt) {
                  return dash;
                }
                const creditGranted = params.row.creditGrantedAt ?? params.row.metadata?.creditGrantedAt;
                const highlightIfPast = !creditGranted && !(new Date(payoutAt) > new Date());
                return <DateFormatterDetailed date={payoutAt} highlightIfPast={highlightIfPast} />;
              },
            },
            buildProcessCreditGrantColumnDef({
              onProcessCreditGrant: handleCreditGrantDryRun,
              onApplyCreditGrant: handleCreditGrantApply,
              processCreditGrantAllowed: isPromoAdmin,
              processCreditGrantPending: creditGrantMutationBusy,
              creditGrantDryRunLoadingLogId,
              creditGrantDryRunReadyLogId,
              creditGrantProcessingLogId,
              creditGrantLastResult,
              creditGrantDryRunLastResult,
            }),
          ];

    const processColumnWhenDiscountOnly: Array<GridColDef<VoucherLog>> =
      displayMode === 'discount-only'
        ? [
            buildProcessCreditGrantColumnDef({
              onProcessCreditGrant: handleCreditGrantDryRun,
              onApplyCreditGrant: handleCreditGrantApply,
              processCreditGrantAllowed: isPromoAdmin,
              processCreditGrantPending: creditGrantMutationBusy,
              creditGrantDryRunLoadingLogId,
              creditGrantDryRunReadyLogId,
              creditGrantProcessingLogId,
              creditGrantLastResult,
              creditGrantDryRunLastResult,
            }),
          ]
        : [];

    const remainingAfterColumns: Array<GridColDef<VoucherLog>> =
      displayMode === 'provide-credit-only'
        ? []
        : [
            {
              field: 'remainingAmountAfter',
              headerName: 'Remaining after discount',
              ...sharedAttrs,
              valueGetter: (_value, row) => row.metadata?.remainingAmountAfter,
              renderCell: (params) => {
                if (isVoucherLogProvideCredit(params.row)) {
                  return dash;
                }
                const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
                return (
                  <Typography>{currencyFormatter(currency, params.row.metadata?.remainingAmountAfter ?? 0)}</Typography>
                );
              },
            },
          ];

    return [
      {
        field: 'createdAt',
        headerName: 'Created At',
        ...sharedAttrs,
        renderCell: (params) => <DateFormatterDetailed date={params.row.createdAt} />,
      },
      {
        field: 'voucherName',
        headerName: 'Voucher Name',
        ...sharedAttrs,
        flex: 1,
        valueGetter: (_value, row) => row.metadata?.voucherName ?? row.metadata?.voucherTitle ?? '',
        renderCell: (params) => {
          const name = params.row.metadata?.voucherName || params.row.metadata?.voucherTitle;
          if (!name) {
            return null;
          }
          return (
            <Stack sx={{ width: '100%', minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => handleFilterByVoucherName(name)}
                title="Click to filter by this voucher name"
              >
                {name}
              </Typography>
              <CopyableField
                value={params.row.voucherId}
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  '& > .MuiIconButton-root': { flexShrink: 0 },
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Box
                      title={params.row.voucherId}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {params.row.voucherId}
                    </Box>
                    <Tooltip title="Open voucher">
                      <IconButton
                        component={RouterLink}
                        to={`/marketing/vouchers/${params.row.voucherId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
                      >
                        <OpenInNew sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            </Stack>
          );
        },
      },
      {
        field: 'userId',
        headerName: 'User',
        ...sharedAttrs,
        renderCell: (params) =>
          params.row.userId ? (
            <PromoCodeUser
              userId={params.row.userId}
              userIdFor={params.row.userId}
              onUserIconClick={() => handleFilterByUserId(params.row.userId)}
            />
          ) : null,
      },
      {
        field: 'fkOrder',
        headerName: 'Order ID',
        ...sharedAttrs,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
              {params.row.fkOrder}
            </Box>
            <Tooltip title="Open order">
              <IconButton
                component={RouterLink}
                to={`/purchases/${params.row.fkOrder}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNew sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      ...amountColumns,
      {
        field: 'itemDiscount',
        headerName: 'Item discount',
        minWidth: 190,
        ...sharedAttrs,
        renderCell: (params) => {
          if (isVoucherLogProvideCredit(params.row)) {
            return dash;
          }
          const itemMetadata = params.row.metadata?.orderItemMetadata ?? params.row.metadata?.itemMetadata;
          const discountResult = itemMetadata?.discountResult;
          const itemAmount =
            discountResult?.voucher_discount_amount ?? discountResult?.promo_code_discount_amount ?? params.row.amount;
          const item: DiscountBreakdownItem = {
            categoryBK: itemMetadata?.categoryBK,
            subCategoryBK: itemMetadata?.subCategoryBK,
            amount: itemAmount,
            discountableTotal: itemMetadata?.discountableTotal,
          };
          return <PromoDiscountBreakdown currencyCode={params.row.currencyCode} items={[item]} column="amount" />;
        },
      },
      {
        field: 'channelMarkupId',
        headerName: 'Channel Markup ID',
        minWidth: 220,
        ...sharedAttrs,
        valueGetter: (_value, row) => getVoucherLogChannelMarkupId(row),
        renderCell: (params) => {
          const channelMarkupId = getVoucherLogChannelMarkupId(params.row);
          return channelMarkupId ? (
            <Link href={`/marketing/channel-markup?channel_markup_id=${encodeURIComponent(channelMarkupId)}`}>
              {channelMarkupId}
            </Link>
          ) : (
            dash
          );
        },
      },
      {
        field: 'itemDiscountableTotal',
        headerName: 'Discountable total',
        minWidth: 150,
        ...sharedAttrs,
        renderCell: (params) => {
          if (isVoucherLogProvideCredit(params.row)) {
            return dash;
          }
          const itemMetadata = params.row.metadata?.orderItemMetadata ?? params.row.metadata?.itemMetadata;
          return (
            <PromoDiscountBreakdown
              currencyCode={params.row.currencyCode}
              items={[{ discountableTotal: itemMetadata?.discountableTotal }]}
              column="discountableTotal"
            />
          );
        },
      },
      {
        field: 'itemDiscountPercentage',
        headerName: 'Discount %',
        minWidth: 110,
        ...sharedAttrs,
        renderCell: (params) => {
          if (isVoucherLogProvideCredit(params.row)) {
            return dash;
          }
          const itemMetadata = params.row.metadata?.orderItemMetadata ?? params.row.metadata?.itemMetadata;
          const discountResult = itemMetadata?.discountResult;
          return (
            <PromoDiscountBreakdown
              items={[
                {
                  amount:
                    discountResult?.voucher_discount_amount ??
                    discountResult?.promo_code_discount_amount ??
                    params.row.amount,
                  discountableTotal: itemMetadata?.discountableTotal,
                },
              ]}
              column="percentage"
            />
          );
        },
      },
      ...processColumnWhenDiscountOnly,
      ...creditTimelineColumns,
      ...remainingAfterColumns,
      {
        field: 'currencyCode',
        headerName: 'Currency',
        ...sharedAttrs,
        flex: 0,
      },
      {
        field: 'expiresAt',
        headerName: 'Expires At',
        ...sharedAttrs,
        valueGetter: (_value, row) => row.metadata?.expiredAt,
        renderCell: (params) =>
          params.row.metadata?.expiredAt ? <DateFormatterDetailed date={params.row.metadata.expiredAt} /> : null,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        ...sharedAttrs,
        renderCell: (params) => <PromoCodeUser userId={params.row.createdBy} userIdFor={params.row.createdBy} />,
      },
      {
        field: 'logWarnings',
        headerName: 'Warnings',
        ...sharedAttrs,
        flex: 0.7,
        valueGetter: (_value, row) => row.metadata?.logWarnings?.length ?? 0,
        renderCell: (params) => <VoucherLogWarningsButton log={params.row} />,
      },
      {
        field: 'deletedAt',
        headerName: 'Deleted At',
        ...sharedAttrs,
        renderCell: (params) =>
          params.row.deletedAt ? (
            <DateFormatterDetailed date={params.row.deletedAt} />
          ) : (
            <Typography variant="body2">Active</Typography>
          ),
      },
      {
        field: 'voucherDescription',
        headerName: 'Description',
        ...sharedAttrs,
        valueGetter: (_value, row) => row.metadata?.voucherDescription,
      },
      {
        field: 'voucherToggles',
        headerName: 'Toggles',
        ...sharedAttrs,
        valueGetter: (_value, row) => row.metadata?.voucherToggles,
        renderCell: (params) => (
          <Typography variant="body2">{params.row.metadata?.voucherToggles?.join(', ')}</Typography>
        ),
      },
      {
        field: 'debug',
        headerName: '',
        ...sharedAttrs,
        flex: 0,
        renderCell: (params) => (
          <DebugModal
            type="generic"
            data={params.row}
            title={`Voucher ${params.row.metadata?.voucherName ?? params.row.voucherId}`}
          />
        ),
      },
      {
        field: 'promoTester',
        headerName: 'Promo tester',
        ...sharedAttrs,
        flex: 2,
        minWidth: 260,
        renderCell: (params) => {
          const orderId = params.row.fkOrder ?? params.row.fk_order;
          const voucherId = params.row.voucherId;
          if (!orderId || !voucherId) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          return (
            <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0, py: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                  Promo tester
                </Typography>
                <PromoTesterLink orderId={String(orderId)} voucherId={String(voucherId)} brand={brand} />
              </Stack>
              <VoucherEvaluationSummary row={params.row} />
            </Stack>
          );
        },
      },
      {
        field: 'voucherLogId',
        headerName: 'Log ID',
        ...sharedAttrs,
      },
      {
        field: 'voucherUserId',
        headerName: 'Voucher User ID',
        ...sharedAttrs,
      },
    ];
  }, [
    displayMode,
    brand,
    handleFilterByVoucherName,
    handleFilterByUserId,
    handleCreditGrantDryRun,
    handleCreditGrantApply,
    isPromoAdmin,
    creditGrantMutationBusy,
    creditGrantDryRunLoadingLogId,
    creditGrantDryRunReadyLogId,
    creditGrantProcessingLogId,
    creditGrantLastResult,
    creditGrantDryRunLastResult,
  ]);

  if (isLoading && !rows.length) {
    return <Spinner size={36} />;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, expanded) => onExpandedChange?.(expanded)}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="voucher-logs"
        id="voucher-logs"
        sx={{
          backgroundColor: 'action.hover',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <PageSubheader title={`Voucher Logs (${total})`}>
          <VoucherLogWarningsSummaryButton warningSummaries={voucherLogWarnings} />
        </PageSubheader>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
          <ColumnSelector
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />
        </Stack>

        <Table
          columns={columns}
          rows={rows}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          totalRowCount={total}
          isLoading={isLoading}
          getRowId={(row: VoucherLog) => row.voucherLogId}
          dataGridProps={{
            getRowHeight: () => 'auto',
            columnVisibilityModel,
            onColumnVisibilityModelChange: setColumnVisibilityModel,
            pageSizeOptions: [10, 20, 50, 100],
            slots: { pagination: GridPagination },
            getRowClassName: (params) => {
              const id = String(params.id);
              if (creditGrantProcessingLogId != null && id === String(creditGrantProcessingLogId)) {
                return 'voucher-log-row-credit-grant-loading';
              }
              if (creditGrantDryRunLoadingLogId != null && id === String(creditGrantDryRunLoadingLogId)) {
                return 'voucher-log-row-credit-grant-loading';
              }
              return '';
            },
            sx: {
              width: '100%',
              '& .MuiDataGrid-row.voucher-log-row-credit-grant-loading': {
                opacity: 0.72,
              },
            },
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default PromoRequestVoucherLogs;
